import FileUpload from "components/FileUpload";
import { useAuth } from "contexts/AuthContext";
import { useToast } from "contexts/ToastContext";
import React, { useState, useRef, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { LoadingIcon } from "utils/constant";
import { 
  LEGAL_ADVISORY_REQUEST_STATUS, 
  LEGAL_RESEARCH_REQUEST_STATUS, 
  LEGISLATIVE_DRAFTING_REQUEST_STATUS 
} from "utils/constantValues";
import { getUserType } from "utils/helpers";
import { createLegalRespondbackFeedback } from "services/legalAdvisoryService";
import { createLegislativeRespondbackFeedback } from "services/legislativeService";
import { createSectorDirectiveRespondbackFeedback, updateSectorDirective } from "services/sectorDirectives";
import { updateLegalAdvisory } from "services/legalAdvisoryService";
import { updateLegislativeDrafting } from "services/legislativeService";

// Define FeedbackAllowedRoles if not imported
const FeedbackAllowedRoles = new Set([
  "LegalResearchCoordinator",
  "LegalResearchDirector", 
  "LegalResearchProsecutor",
  "LegalResearchHead"
]);

interface UploadProposalProps {
    request: any;
    isOpen: boolean;
    onClose: () => void;
    setLegalResearch: Function;
    forward: any;
    type?: string;
}

const LegislativeRespondBackForm: React.FC<UploadProposalProps> = ({
    request,
    isOpen,
    setLegalResearch,
    onClose,
    forward,
    type,
}) => {
    const [proposalSummary, setProposalSummary] = useState(request.proposal_summary || "");
    const [proposalFile, setProposalFile] = useState<File | null>(null);
    const [submitting, setSubmitting] = useState(false);
    const [validationError, setValidationError] = useState<string | null>(null);
    
    const { showToast } = useToast();
    const { t } = useTranslation();
    const contextData = useAuth();
    const modalContentRef = useRef<HTMLDivElement>(null);

    // Handle clicks outside the modal
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (
                modalContentRef.current &&
                !modalContentRef.current.contains(event.target as Node)
            ) {
                onClose();
            }
        };

        if (isOpen) {
            document.addEventListener("mousedown", handleClickOutside);
        }

        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, [isOpen, onClose]);

    if (!isOpen) return null;

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setValidationError(null);
        setSubmitting(true);

        try {
            const data = new FormData();
            
            // Set feedback type and request ID based on request type
            if (type === "legal") {
                data.append("advisory_request", request.id);
                data.append("feedback_type", "Review");
            } else if (type === "sector") {
                data.append("feedback", request.id);
                data.append("feedback_type", "Review");
            } else {
                data.append("instruction", request.id);
                data.append("feedback_type", "Additional_info");
            }
            
            // Add feedback role if applicable
            const feedbackRole = contextData?.user?.roles?.find((role: string) => FeedbackAllowedRoles.has(role));
            if (feedbackRole) {
                data.append("role", getUserType(feedbackRole));
            }
            
            data.append("note", proposalSummary);
            data.append("given_by", contextData?.user?.full_name || "");
            
            if (proposalFile) {
                data.append("respond_back_feedback_files", proposalFile);
            }

            let response;
            const token = contextData.token || contextData?.user?.access_token || "";
            
            if (type === "legal") {
                response = await createLegalRespondbackFeedback(data);
            } else if (type === "sector") {
                response = await createSectorDirectiveRespondbackFeedback(data, token);
            } else {
                response = await createLegislativeRespondbackFeedback(data);
            }
            
            // Update request status
            const researchData = new FormData();
            let researchResponse: any;
            
            // Respond back always sends the request back to the prosecutor.
            researchData.append("forward_status", LEGAL_RESEARCH_REQUEST_STATUS.assigned_to_prosecutor.value);
            
            if (type === "legal") {
                researchData.append("status", forward);
                researchResponse = await updateLegalAdvisory(request.id, researchData);
            } else if (type === "sector") {
                researchData.append("status", forward);
                researchResponse = await updateSectorDirective(request.id, researchData, token);
            } else {
                researchData.append("current_status", forward);
                researchResponse = await updateLegislativeDrafting(request.id, researchData);
            }
            
            setLegalResearch((prev: any) => ({
                ...prev,
                ...researchResponse,
            }));
            
            onClose();
            showToast(t("RespondBackSubmitted"), "success");
        } catch (error) {
            console.error("Error submitting request:", error);
            showToast(t("FailedToUploadFile"), "error");
        } finally {
            setSubmitting(false);
        }
    };

    const handleFileChange = (file: File | null) => {
        setProposalFile(file);
    };

    return (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 flex justify-end p-4 z-50 overflow-hidden">
            <div
                ref={modalContentRef}
                className="bg-white rounded-lg shadow-xl w-full max-w-lg p-6 relative flex flex-col h-full my-auto overflow-y-auto pr-2 custom-scrollbar"
            >
                <button
                    onClick={onClose}
                    className="absolute top-4 right-4 text-gray-400 hover:text-gray-600"
                >
                    <svg
                        xmlns="http://www.w3.org/2000/svg"
                        className="h-6 w-6"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                    >
                        <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M6 18L18 6M6 6l12 12"
                        />
                    </svg>
                </button>

                <h2 className="text-xl font-semibold text-gray-800 mb-6">
                    {t("RespondBack")}
                </h2>

                <form onSubmit={handleSubmit} className="flex flex-col">
                    <div className="flex-grow overflow-y-auto pr-2 custom-scrollbar">
                        <div className="mb-4">
                            <label
                                htmlFor="remark"
                                className="block text-sm font-medium text-gray-700 mb-2"
                            >
                                {t("RespondBackFeedback")}
                            </label>
                            <textarea
                                id="remark"
                                className="w-full p-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-gray-800 focus:border-transparent resize-y min-h-[100px]"
                                placeholder={t("DetailHere")}
                                value={proposalSummary}
                                onChange={(e) => setProposalSummary(e.target.value)}
                            />
                        </div>

                        <div className="mb-6">
                            <label
                                htmlFor="deadline"
                                className="block text-sm font-medium text-gray-700 mb-2"
                            >
                                {t("RespondBackFile")}
                            </label>
                            <div className="relative">
                                <FileUpload onChange={handleFileChange} />
                            </div>
                        </div>
                    </div>
                    
                    {validationError && (
                        <p className="text-red-600 text-sm mb-4">{validationError}</p>
                    )}
                    
                    <div className="flex space-x-4 pt-4 border-t border-gray-200">
                        <button
                            type="submit"
                            disabled={submitting}
                            className="w-full bg-gray-800 hover:bg-[#13294d] text-white font-bold py-3 px-6 rounded-md disabled:opacity-70 flex items-center justify-center gap-2"
                        >
                            {submitting ? (
                                <>
                                    <svg 
                                        className="animate-spin h-4 w-4 text-white" 
                                        xmlns="http://www.w3.org/2000/svg" 
                                        fill="none" 
                                        viewBox="0 0 24 24"
                                    >
                                        <circle 
                                            className="opacity-25" 
                                            cx="12" 
                                            cy="12" 
                                            r="10" 
                                            stroke="currentColor" 
                                            strokeWidth="4"
                                        />
                                        <path 
                                            className="opacity-75" 
                                            fill="currentColor" 
                                            d="M4 12a8 8 0 018-8v8z"
                                        />
                                    </svg>
                                    {t("Submitting")}
                                </>
                            ) : t("Submit")}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default LegislativeRespondBackForm;